(() => {
  let pickerState = null;
  let pickerOpenRequest = 0;
  let recommendationReconcileTimer = null;

  const queryPicker = () => document.querySelector('.lunicora-cart-drawer [data-variant-picker]');

  const getCarouselParts = (button) => {
    const carousel = button.closest('[data-lunicora-carousel]');
    const viewport = carousel?.querySelector('[data-carousel-viewport]');
    const card = viewport?.querySelector('.lunicora-featured__card');
    return { carousel, viewport, card };
  };

  const updateCarouselControls = (carousel) => {
    const viewport = carousel?.querySelector('[data-carousel-viewport]');
    if (!viewport) return;

    const previous = carousel.querySelector('[data-carousel-previous]');
    const next = carousel.querySelector('[data-carousel-next]');
    const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const hasOverflow = maximum > 2;

    carousel.classList.toggle('lunicora-featured--scrollable', hasOverflow);

    if (previous) {
      previous.hidden = !hasOverflow;
      previous.disabled = viewport.scrollLeft <= 1;
    }
    if (next) {
      next.hidden = !hasOverflow;
      next.disabled = viewport.scrollLeft >= maximum - 1;
    }
  };

  const interleaveCarouselGroups = (carousel) => {
    if (carousel.dataset.carouselInterleave !== 'true' || carousel.dataset.groupsInterleaved === 'true') return;

    const track = carousel.querySelector('.lunicora-featured__track');
    if (!track) return;

    const groups = new Map();
    track.querySelectorAll('[data-recommendation-group]').forEach((card) => {
      const group = card.dataset.recommendationGroup;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(card);
    });

    const groupCards = Array.from(groups.values());
    if (groupCards.length > 1) {
      const fallbackCards = Array.from(track.querySelectorAll('[data-recommendation-fallback]'));
      const longestGroup = Math.max(...groupCards.map((cards) => cards.length));
      for (let cardIndex = 0; cardIndex < longestGroup; cardIndex += 1) {
        groupCards.forEach((cards) => {
          if (cards[cardIndex]) track.appendChild(cards[cardIndex]);
        });
      }
      fallbackCards.forEach((card) => track.appendChild(card));
    }

    carousel.dataset.groupsInterleaved = 'true';
  };

  const randomizeFallbackCards = (carousel) => {
    if (carousel.dataset.fallbacksRandomized === 'true') return;

    const track = carousel.querySelector('.lunicora-featured__track');
    const fallbackCards = Array.from(track?.querySelectorAll('[data-recommendation-fallback]') || []);
    for (let cardIndex = fallbackCards.length - 1; cardIndex > 0; cardIndex -= 1) {
      const randomIndex = Math.floor(Math.random() * (cardIndex + 1));
      [fallbackCards[cardIndex], fallbackCards[randomIndex]] = [fallbackCards[randomIndex], fallbackCards[cardIndex]];
    }
    const fallbackLimit = Number.parseInt(carousel.dataset.carouselFallbackLimit || '', 10);
    fallbackCards.forEach((card, cardIndex) => {
      if (Number.isFinite(fallbackLimit) && fallbackLimit >= 0 && cardIndex >= fallbackLimit) card.remove();
      else track.appendChild(card);
    });
    carousel.dataset.fallbacksRandomized = 'true';
  };

  const limitCarouselCards = (carousel) => {
    const itemLimit = Number.parseInt(carousel.dataset.carouselLimit || '', 10);
    if (!Number.isFinite(itemLimit) || itemLimit <= 0) return;

    carousel.querySelectorAll('.lunicora-featured__card').forEach((card, cardIndex) => {
      if (cardIndex >= itemLimit) card.remove();
    });
  };

  // ---- Auto-scroll for the recommendation carousels ----
  // Driven by the cart drawer settings "Auto-scroll the recommendation carousels"
  // / "Auto-scroll speed" (data-carousel-autoscroll / -speed on the carousel).
  // Glides from right to left while on screen; the first card is moved behind
  // the last one as soon as it has left the viewport, so the loop is endless.
  // The first hover, touch, click, key press, focus or horizontal swipe stops it
  // for good.
  const carouselAutoscrollStates = new WeakMap();
  const autoscrollStopEvents = ['pointerdown', 'touchstart', 'click', 'keydown', 'focusin', 'mouseenter'];

  const stopCarouselAutoscroll = (carousel) => {
    const state = carouselAutoscrollStates.get(carousel);
    if (!state || state.stopped) return;
    state.stopped = true;
    if (state.frame) window.cancelAnimationFrame(state.frame);
    state.frame = null;
    state.observer?.disconnect();
    state.observer = null;
    // Snapping stays off so the cards do not jump when the customer takes over;
    // smooth scrolling comes back for the arrows.
    state.viewport.style.scrollBehavior = '';
  };

  const stepCarouselAutoscroll = (carousel, now) => {
    const state = carouselAutoscrollStates.get(carousel);
    if (!state || state.stopped) return;
    if (!carousel.isConnected) {
      stopCarouselAutoscroll(carousel);
      return;
    }
    if (!state.visible || document.hidden) {
      state.frame = null;
      state.lastTime = null;
      return;
    }

    const { viewport } = state;
    const track = viewport.querySelector('.lunicora-featured__track');
    const cards = track ? Array.from(track.children).filter((node) => node.classList.contains('lunicora-featured__card')) : [];
    const maximum = viewport.scrollWidth - viewport.clientWidth;
    if (cards.length < 2 || maximum <= 2) {
      state.frame = null;
      state.lastTime = null;
      return;
    }

    const elapsed = state.lastTime == null ? 0 : Math.min(100, now - state.lastTime);
    state.lastTime = now;
    state.position += (state.speed * elapsed) / 1000;

    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const firstWidth = cards[0].getBoundingClientRect().width + gap;
    if (firstWidth > 0 && state.position >= firstWidth) {
      track.appendChild(cards[0]);
      state.position -= firstWidth;
    }
    viewport.scrollLeft = state.position;
    state.frame = window.requestAnimationFrame((time) => stepCarouselAutoscroll(carousel, time));
  };

  const resumeCarouselAutoscroll = (carousel) => {
    const state = carouselAutoscrollStates.get(carousel);
    if (!state || state.stopped || !state.visible || state.frame || document.hidden) return;
    // Snapping and smooth scrolling would fight the per-frame position updates.
    state.viewport.style.scrollSnapType = 'none';
    state.viewport.style.scrollBehavior = 'auto';
    state.position = state.viewport.scrollLeft;
    state.lastTime = null;
    state.frame = window.requestAnimationFrame((time) => stepCarouselAutoscroll(carousel, time));
  };

  const setupCarouselAutoscroll = (carousel) => {
    if (carousel.dataset.carouselAutoscroll !== 'true' || carouselAutoscrollStates.has(carousel)) return;
    const viewport = carousel.querySelector('[data-carousel-viewport]');
    if (!viewport) return;

    const speed = Number.parseFloat(carousel.dataset.carouselAutoscrollSpeed);
    const state = {
      viewport,
      speed: Number.isFinite(speed) && speed > 0 ? speed : 25,
      position: 0,
      lastTime: null,
      frame: null,
      visible: false,
      stopped: false,
      observer: null,
    };
    carouselAutoscrollStates.set(carousel, state);

    const stop = () => stopCarouselAutoscroll(carousel);
    autoscrollStopEvents.forEach((type) => carousel.addEventListener(type, stop, { capture: true, passive: true }));
    carousel.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) stop();
    }, { capture: true, passive: true });

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        state.visible = Boolean(entry && entry.isIntersecting && entry.intersectionRatio >= 0.35);
        if (state.visible) resumeCarouselAutoscroll(carousel);
      }, { threshold: [0, 0.35, 0.7] });
      state.observer.observe(carousel);
    } else {
      state.visible = true;
      resumeCarouselAutoscroll(carousel);
    }
  };

  if (!document.documentElement.dataset.lunicoraCarouselAutoscrollReady) {
    document.documentElement.dataset.lunicoraCarouselAutoscrollReady = 'true';
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      document.querySelectorAll('[data-lunicora-carousel]').forEach((carousel) => resumeCarouselAutoscroll(carousel));
    });
  }

  const initializeCarousels = (root = document) => {
    const carousels = [];
    if (root.matches?.('[data-lunicora-carousel]')) carousels.push(root);
    root.querySelectorAll?.('[data-lunicora-carousel]').forEach((carousel) => carousels.push(carousel));

    carousels.forEach((carousel) => {
      randomizeFallbackCards(carousel);
      interleaveCarouselGroups(carousel);
      limitCarouselCards(carousel);
      if (carousel.dataset.controlsReady !== 'true') {
        carousel.dataset.controlsReady = 'true';
        const viewport = carousel.querySelector('[data-carousel-viewport]');
        viewport?.addEventListener('scroll', () => updateCarouselControls(carousel), { passive: true });
      }
      updateCarouselControls(carousel);
      setupCarouselAutoscroll(carousel);
    });
  };

  const removeCartProductsFromRecommendations = (cart) => {
    const cartProductIds = new Set((cart?.items || []).map((item) => String(item.product_id)));
    const cartProductHandles = new Set((cart?.items || []).map((item) => item.handle).filter(Boolean));

    document.querySelectorAll('.lunicora-cart-drawer [data-recommendation-card]').forEach((card) => {
      const productId = card.dataset.recommendationProductId;
      const productHandle = card.dataset.recommendationProductHandle;
      if (cartProductIds.has(productId) || cartProductHandles.has(productHandle)) card.remove();
    });

    document.querySelectorAll('.lunicora-cart-drawer [data-lunicora-carousel]').forEach((carousel) => {
      if (!carousel.querySelector('[data-recommendation-card]')) carousel.remove();
      else updateCarouselControls(carousel);
    });
  };

  const fetchCart = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  };

  const scheduleRecommendationReconcile = ({ productId = '', attempt = 0 } = {}) => {
    window.clearTimeout(recommendationReconcileTimer);
    recommendationReconcileTimer = window.setTimeout(async () => {
      try {
        const cart = await fetchCart();
        removeCartProductsFromRecommendations(cart);
        const productWasAdded = !productId
          || (cart.items || []).some((item) => String(item.product_id) === String(productId));
        if (!productWasAdded && attempt < 8) {
          scheduleRecommendationReconcile({ productId, attempt: attempt + 1 });
        }
      } catch (_error) {
        if (attempt < 3) scheduleRecommendationReconcile({ productId, attempt: attempt + 1 });
      }
    }, attempt === 0 ? 250 : 500);
  };

  const normalizeOptionValue = (value) => {
    if (typeof value === 'string') return value;
    return value?.name || value?.value || String(value || '');
  };

  const getCompleteProductOptions = (product) => {
    const configuredOptions = Array.isArray(product.options) ? product.options : [];
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variantOptionCount = variants.reduce((count, variant) => (
      Math.max(count, Array.isArray(variant.options) ? variant.options.length : 0)
    ), 0);
    const optionCount = Math.max(configuredOptions.length, variantOptionCount);

    return Array.from({ length: optionCount }, (_, optionIndex) => {
      const configuredOption = configuredOptions[optionIndex] || {};
      const values = [];
      const addValue = (rawValue) => {
        const value = normalizeOptionValue(rawValue).trim();
        if (value && !values.includes(value)) values.push(value);
      };

      (configuredOption.values || []).forEach(addValue);
      variants.forEach((variant) => addValue(variant.options?.[optionIndex]));

      return {
        name: configuredOption.name || `Option ${optionIndex + 1}`,
        position: configuredOption.position || optionIndex + 1,
        values,
      };
    });
  };

  const parseJsonSetting = (value, fallback) => {
    if (typeof value !== 'string') return value ?? fallback;
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  };

  const normalizeResourceId = (value) => String(value || '').split('/').pop();

  const productMatchesEasifyCondition = (product, condition) => {
    const conditionType = String(condition?.select || '').toUpperCase();
    const relative = String(condition?.relative || 'EQUAL').toUpperCase();
    const rawExpected = condition?.value;
    const expected = conditionType === 'COLLECTION'
      ? normalizeResourceId(rawExpected?.id || rawExpected)
      : String(rawExpected?.title || rawExpected?.name || rawExpected || '').trim();
    let actualValues = [];

    if (conditionType === 'COLLECTION') {
      actualValues = (product.collectionIds || []).map(normalizeResourceId);
    } else if (conditionType === 'TAG') {
      actualValues = product.tags || [];
    } else if (conditionType === 'TITLE') {
      actualValues = [product.title];
    } else if (conditionType === 'VENDOR') {
      actualValues = [product.vendor];
    } else if (conditionType === 'TYPE' || conditionType === 'PRODUCT_TYPE') {
      actualValues = [product.productType];
    } else if (conditionType === 'PRODUCT' || conditionType === 'ID') {
      actualValues = [normalizeResourceId(product.id)];
    } else if (conditionType === 'HANDLE') {
      actualValues = [product.handle];
    }

    const normalizedExpected = expected.toLowerCase();
    const normalizedActualValues = actualValues.map((value) => String(value || '').trim().toLowerCase());
    const equals = normalizedActualValues.some((value) => value === normalizedExpected);
    const contains = normalizedActualValues.some((value) => value.includes(normalizedExpected));
    const startsWith = normalizedActualValues.some((value) => value.startsWith(normalizedExpected));
    const endsWith = normalizedActualValues.some((value) => value.endsWith(normalizedExpected));

    if (relative.includes('NOT') && relative.includes('CONTAIN')) return !contains;
    if (relative.includes('NOT')) return !equals;
    if (relative.includes('CONTAIN')) return contains;
    if (relative.includes('START')) return startsWith;
    if (relative.includes('END')) return endsWith;
    return equals;
  };

  const productMatchesEasifySet = (product, optionSet) => {
    if (!optionSet || Number(optionSet.status) !== 1) return false;

    if (Number(optionSet.apply_type) === 1) {
      const productId = normalizeResourceId(product.id);
      const explicitIds = (optionSet.option_set_products || []).map((item) => normalizeResourceId(item.product_id));
      const explicitHandles = parseJsonSetting(optionSet.products_handle, optionSet.products_handle || []);
      return explicitIds.includes(productId)
        || (Array.isArray(explicitHandles) && explicitHandles.includes(product.handle));
    }

    const conditionConfig = parseJsonSetting(optionSet.product_condition, {});
    const conditions = Array.isArray(conditionConfig?.conditions) ? conditionConfig.conditions : [];
    if (!conditions.length) return false;
    const results = conditions.map((condition) => productMatchesEasifyCondition(product, condition));
    return String(conditionConfig.operator || 'any').toLowerCase() === 'all'
      ? results.every(Boolean)
      : results.some(Boolean);
  };

  const getEasifyColorOptionsLegacy = (product) => {
    const configurations = window.TPOConfigs?.product_options_easify;
    if (!Array.isArray(configurations)) return [];

    const optionsByKey = new Map();
    configurations.forEach((configuration) => {
      const optionSet = configuration?.option_set;
      if (!productMatchesEasifySet(product, optionSet)) return;

      (optionSet.options || []).forEach((option) => {
        if (Number(option.is_active_option) !== 1 || option.type !== 'color-swatches') return;
        const key = String(option.option_identifier || option.title || 'Color').trim();
        const values = (option.option_types || []).map((value) => ({
          label: String(value.label || '').trim(),
          color: String(value.color_code || '').split(',')[0].trim(),
          isDefault: Number(value.is_default) === 1,
        })).filter((value) => value.label);
        if (!values.length) return;

        optionsByKey.set(key.toLowerCase(), {
          key,
          name: String(option.title || key).trim(),
          required: Number(option.is_required) === 1,
          values,
        });
      });
    });

    return Array.from(optionsByKey.values());
  };

  /*
    Every option a shopper can fill in on the product page (Easify swatches,
    buttons, dropdowns, text boxes), read by the shared lunicora-easify-options
    reader so the gift carousel and this picker agree. The colour-only reader
    above stays as a fallback for when the shared file has not loaded.
  */
  const getEasifyCustomOptions = (product) => (
    window.LunicoraEasify ? window.LunicoraEasify.getCustomOptions(product) : getEasifyColorOptionsLegacy(product)
  );
  const hasExternalPersonalizer = (product) => (
    window.LunicoraEasify ? window.LunicoraEasify.hasCustomilyPersonalizer(product) : Promise.resolve(false)
  );

  const normalizePreferenceKey = (value) => String(value || '').trim().toLowerCase();

  const getCartItems = async () => {
    try {
      const shopRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${shopRoot}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return [];
      const cart = await response.json();
      return Array.isArray(cart.items) ? cart.items : [];
    } catch (_error) {
      return [];
    }
  };

  const getCartPreferenceMatch = (cartItems, options, customOptions) => {
    const matches = cartItems.map((item, cartIndex) => {
      const valuesByName = new Map();
      (item.options_with_values || []).forEach((option) => {
        const key = normalizePreferenceKey(option.name);
        const value = normalizeOptionValue(option.value).trim();
        if (key && value) valuesByName.set(key, value);
      });
      Object.entries(item.properties || {}).forEach(([name, rawValue]) => {
        if (String(name).startsWith('_')) return;
        const key = normalizePreferenceKey(name);
        const value = normalizeOptionValue(rawValue).trim();
        if (key && value && !valuesByName.has(key)) valuesByName.set(key, value);
      });

      const nativeSelections = options.map((option) => {
        const savedValue = valuesByName.get(normalizePreferenceKey(option.name));
        return option.values.find((value) => normalizePreferenceKey(value) === normalizePreferenceKey(savedValue)) || null;
      });
      const customSelections = {};
      customOptions.forEach((option) => {
        const savedValue = valuesByName.get(normalizePreferenceKey(option.name))
          || valuesByName.get(normalizePreferenceKey(option.key));
        customSelections[option.key] = option.values.find((value) => (
          normalizePreferenceKey(value.label) === normalizePreferenceKey(savedValue)
        ))?.label || null;
      });

      const nativeScore = nativeSelections.filter(Boolean).length * 4;
      const customScore = Object.values(customSelections).filter(Boolean).length * 3;
      return { nativeSelections, customSelections, score: nativeScore + customScore, cartIndex };
    }).filter((match) => match.score > 0);

    matches.sort((left, right) => right.score - left.score || left.cartIndex - right.cartIndex);
    return matches[0] || {
      nativeSelections: options.map(() => null),
      customSelections: Object.fromEntries(customOptions.map((option) => [option.key, null])),
      score: 0,
    };
  };

  const formatMoney = (cents, currency) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2)} ${currency || ''}`.trim();
    }
  };

  const getVariantImage = (variant, fallback) => {
    const source = variant?.featured_image?.src
      || variant?.featured_media?.preview_image?.src
      || fallback
      || '';
    return source.startsWith('//') ? `${window.location.protocol}${source}` : source;
  };

  const variantMatches = (variant, selections, candidateIndex = -1, candidateValue = null) => {
    return selections.every((selection, index) => {
      const expected = index === candidateIndex ? candidateValue : selection;
      return expected == null || variant.options?.[index] === expected;
    });
  };

  const getExactVariant = () => {
    if (!pickerState || pickerState.selections.some((selection) => selection == null)) return null;
    return pickerState.product.variants.find((variant) => variant.options.every(
      (value, index) => value === pickerState.selections[index]
    )) || null;
  };

  const closePickerOptionMenus = (picker = pickerState?.picker) => {
    if (!picker) return;
    picker.querySelectorAll('[data-variant-picker-option-menu]').forEach((menu) => {
      menu.hidden = true;
    });
    picker.querySelectorAll('[data-variant-picker-option-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  };

  const togglePickerOptionMenu = (trigger) => {
    const state = pickerState;
    if (!state || !trigger) return;

    const menu = state.picker.querySelector(
      `[data-variant-picker-option-menu="${trigger.dataset.variantPickerOptionTrigger}"]`,
    );
    if (!menu) return;

    const wasOpen = !menu.hidden;
    closePickerOptionMenus(state.picker);
    if (wasOpen) return;

    menu.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    const dialogRect = state.picker.querySelector('[data-variant-picker-dialog]')?.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const desiredHeight = Math.min(menu.querySelectorAll('button').length * 4.4 * 16, 27 * 16);
    const spaceBelow = dialogRect ? dialogRect.bottom - triggerRect.bottom - 8 : desiredHeight;
    const spaceAbove = dialogRect ? triggerRect.top - dialogRect.top - 8 : 0;
    const openUp = dialogRect && spaceBelow < desiredHeight && spaceAbove > spaceBelow;
    const availableSpace = openUp ? spaceAbove : spaceBelow;
    menu.dataset.menuPlacement = openUp ? 'up' : 'down';
    menu.style.maxHeight = `${Math.max(11.2 * 16, Math.min(desiredHeight, availableSpace))}px`;
  };

  const updatePicker = () => {
    if (!pickerState) return;
    const { picker, product, selections, customOptions, customSelections } = pickerState;

    picker.querySelectorAll('[data-option-index]').forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue;
      const possible = product.variants.some((variant) => (
        variant.available && variantMatches(variant, selections, optionIndex, optionValue)
      ));
      const selected = selections[optionIndex] === optionValue;
      button.disabled = !possible;
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-selected', selected);
    });

    picker.querySelectorAll('[data-option-select-index]').forEach((select) => {
      const optionIndex = Number(select.dataset.optionSelectIndex);
      Array.from(select.options).forEach((optionElement) => {
        if (!optionElement.value) return;
        optionElement.disabled = !product.variants.some((variant) => (
          variant.available && variantMatches(variant, selections, optionIndex, optionElement.value)
        ));
      });
      select.value = selections[optionIndex] || '';
    });

    picker.querySelectorAll('[data-variant-picker-option-menu][data-option-menu-index]').forEach((menu) => {
      const optionIndex = Number(menu.dataset.optionMenuIndex);
      const trigger = picker.querySelector(`[data-variant-picker-option-trigger="native-${optionIndex}"]`);
      if (trigger) trigger.textContent = selections[optionIndex] || trigger.dataset.optionPlaceholder || 'Choose option';
      menu.querySelectorAll('[data-option-menu-value]').forEach((optionButton) => {
        const optionValue = optionButton.dataset.optionMenuValue;
        const possible = product.variants.some((variant) => (
          variant.available && variantMatches(variant, selections, optionIndex, optionValue)
        ));
        const selected = selections[optionIndex] === optionValue;
        optionButton.disabled = !possible;
        optionButton.setAttribute('aria-pressed', String(selected));
        optionButton.classList.toggle('is-selected', selected);
      });
    });

    picker.querySelectorAll('[data-custom-option-key]').forEach((button) => {
      const selected = customSelections[button.dataset.customOptionKey] === button.dataset.customOptionValue;
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-selected', selected);
    });

    picker.querySelectorAll('[data-custom-option-select-key]').forEach((select) => {
      const option = customOptions.find((customOption) => customOption.key === select.dataset.customOptionSelectKey);
      Array.from(select.options).forEach((optionElement) => {
        if (!optionElement.value) return;
        optionElement.disabled = !option?.values.some((value) => value.label === optionElement.value);
      });
      select.value = customSelections[select.dataset.customOptionSelectKey] || '';
    });

    picker.querySelectorAll('[data-variant-picker-option-menu][data-custom-option-menu-key]').forEach((menu) => {
      const optionKey = menu.dataset.customOptionMenuKey;
      const trigger = picker.querySelector(`[data-variant-picker-option-trigger="custom-${optionKey}"]`);
      if (trigger) trigger.textContent = customSelections[optionKey] || trigger.dataset.optionPlaceholder || 'Choose option';
      menu.querySelectorAll('[data-custom-option-menu-value]').forEach((optionButton) => {
        const selected = customSelections[optionKey] === optionButton.dataset.customOptionMenuValue;
        optionButton.setAttribute('aria-pressed', String(selected));
        optionButton.classList.toggle('is-selected', selected);
      });
    });

    picker.querySelectorAll('[data-custom-option-selection-key]').forEach((label) => {
      label.textContent = customSelections[label.dataset.customOptionSelectionKey] || '';
    });

    picker.querySelectorAll('[data-custom-option-text-key]').forEach((input) => {
      if (document.activeElement !== input) input.value = customSelections[input.dataset.customOptionTextKey] || '';
    });

    const exactVariant = getExactVariant();
    const submit = picker.querySelector('[data-variant-picker-submit]');
    const status = picker.querySelector('[data-variant-picker-status]');
    const price = picker.querySelector('[data-variant-picker-price]');
    const compare = picker.querySelector('[data-variant-picker-compare]');
    const image = picker.querySelector('[data-variant-picker-image]');
    const matchingPreviewVariant = product.variants.find((variant) => (
      variant.available && variantMatches(variant, selections)
    )) || product.variants.find((variant) => variantMatches(variant, selections));
    const displayVariant = exactVariant
      || matchingPreviewVariant
      || product.variants.find((variant) => variant.available)
      || product.variants[0];

    if (displayVariant) {
      price.textContent = formatMoney(displayVariant.price, product.currency);
      const hasCompare = Number(displayVariant.compare_at_price) > Number(displayVariant.price);
      compare.hidden = !hasCompare;
      compare.textContent = hasCompare ? formatMoney(displayVariant.compare_at_price, product.currency) : '';
      image.src = getVariantImage(displayVariant, product.featuredImage);
    }

    const missingCustomOption = customOptions.find((option) => option.required && !customSelections[option.key]);

    const nameValue = String(picker.querySelector('[data-variant-picker-name]')?.value || '').trim();

    if (pickerState.externalPersonalizer) {
      submit.disabled = false;
      submit.textContent = 'Personalize on product page';
      status.textContent = 'This item is personalized on its own page';
    } else if (!exactVariant) {
      submit.disabled = true;
      submit.textContent = 'Add to cart';
      status.textContent = 'Choose all options';
    } else if (missingCustomOption) {
      submit.disabled = true;
      submit.textContent = 'Add to cart';
      status.textContent = `Choose ${missingCustomOption.name}`;
    } else if (pickerState.nameRequired && !nameValue) {
      submit.disabled = true;
      submit.textContent = 'Add to cart';
      status.textContent = 'Enter a name';
    } else if (!exactVariant.available) {
      submit.disabled = true;
      submit.textContent = 'Sold out';
      status.textContent = 'This combination is sold out';
    } else {
      submit.disabled = false;
      submit.textContent = 'Add to cart';
      status.textContent = 'In stock';
    }
  };

  const closePicker = ({ returnFocus = true } = {}) => {
    pickerOpenRequest += 1;
    const state = pickerState;
    if (!state) return;
    state.picker.hidden = true;
    document.documentElement.classList.remove('lunicora-variant-picker-open');
    pickerState = null;
    if (returnFocus && state.trigger?.isConnected) state.trigger.focus();
  };

  const openPicker = async (card, trigger) => {
    const requestId = ++pickerOpenRequest;
    const picker = queryPicker();
    const dataElement = card.querySelector('[data-recommendation-product-json]');
    if (!picker || !dataElement) return;

    let product;
    try {
      product = JSON.parse(dataElement.textContent);
    } catch (_error) {
      return;
    }

    const options = getCompleteProductOptions(product);
    const customOptions = getEasifyCustomOptions(product);
    const hasSingleVariant = product.variants.length === 1;
    const useMobileOptionMenus = window.matchMedia('(max-width: 749px)').matches;
    trigger.setAttribute('aria-busy', 'true');
    const cartItems = await getCartItems();
    // Customily products are personalised on their own page; the picker
    // becomes a hand-off instead of adding an unfinished line.
    const externalPersonalizer = await hasExternalPersonalizer(product);
    trigger.removeAttribute('aria-busy');
    if (requestId !== pickerOpenRequest || !card.isConnected) return;
    const requiredTemplates = String(picker.dataset.nameRequiredTemplates || '').toLowerCase().split(',').map((value) => value.trim()).filter(Boolean);
    const nameRequired = requiredTemplates.includes(String(product.templateSuffix || '').trim().toLowerCase());
    const cartPreference = getCartPreferenceMatch(cartItems, options, customOptions);
    const selections = options.map(() => null);
    cartPreference.nativeSelections.forEach((savedValue, optionIndex) => {
      if (!savedValue) return;
      const remainsAvailable = product.variants.some((variant) => (
        variant.available && variantMatches(variant, selections, optionIndex, savedValue)
      ));
      if (remainsAvailable) selections[optionIndex] = savedValue;
    });
    if (hasSingleVariant) {
      options.forEach((option, optionIndex) => {
        selections[optionIndex] = normalizeOptionValue(option.values?.[0]);
      });
    }
    const customSelections = { ...cartPreference.customSelections };
    pickerState = {
      picker,
      card,
      trigger,
      product,
      customOptions,
      customSelections,
      selections,
      nameRequired,
      externalPersonalizer,
    };

    picker.querySelector('[data-variant-picker-title]').textContent = product.title;
    const image = picker.querySelector('[data-variant-picker-image]');
    image.alt = product.title;
    const error = picker.querySelector('[data-variant-picker-error]');
    error.hidden = true;
    error.textContent = '';
    const nameInput = picker.querySelector('[data-variant-picker-name]');
    const nameCount = picker.querySelector('[data-variant-picker-name-count]');
    if (nameInput) nameInput.value = '';
    if (nameCount) nameCount.textContent = '0/12';
    const nameField = picker.querySelector('.lunicora-variant-picker__name-field');
    const nameHint = nameField?.querySelector('label span');
    if (nameHint) nameHint.textContent = nameRequired ? '*' : '(Optional)';
    if (nameField) nameField.hidden = externalPersonalizer;

    const optionsContainer = picker.querySelector('[data-variant-picker-options]');
    optionsContainer.replaceChildren();
    optionsContainer.hidden = externalPersonalizer;
    optionsContainer.style.display = externalPersonalizer ? "none" : "";
    if (nameField) nameField.style.display = externalPersonalizer ? "none" : "";
    options.forEach((option, optionIndex) => {
      if (hasSingleVariant) return;
      const group = document.createElement('fieldset');
      group.className = 'lunicora-variant-picker__option';
      group.dataset.variantOptionName = option.name;
      const optionName = String(option.name || '').trim().toLowerCase();
      if (optionName === 'type') group.classList.add('lunicora-variant-picker__option--type');
      if (optionName === 'size') group.classList.add('lunicora-variant-picker__option--size');

      const legend = document.createElement('legend');
      legend.textContent = option.name || `Option ${optionIndex + 1}`;
      group.appendChild(legend);

      const values = document.createElement('div');
      values.className = 'lunicora-variant-picker__values lunicora-variant-picker__values--select';

      if (useMobileOptionMenus) {
        const menuKey = `native-${optionIndex}`;
        const triggerButton = document.createElement('button');
        triggerButton.type = 'button';
        triggerButton.className = 'lunicora-variant-picker__menu-trigger';
        triggerButton.dataset.variantPickerOptionTrigger = menuKey;
        triggerButton.dataset.optionPlaceholder = `Choose ${option.name || `option ${optionIndex + 1}`}`;
        triggerButton.setAttribute('aria-expanded', 'false');
        triggerButton.setAttribute('aria-haspopup', 'listbox');
        triggerButton.textContent = triggerButton.dataset.optionPlaceholder;

        const menu = document.createElement('div');
        menu.className = 'lunicora-variant-picker__menu';
        menu.dataset.variantPickerOptionMenu = menuKey;
        menu.dataset.optionMenuIndex = String(optionIndex);
        menu.setAttribute('role', 'listbox');
        menu.hidden = true;

        (option.values || []).forEach((rawValue) => {
          const value = normalizeOptionValue(rawValue);
          const optionButton = document.createElement('button');
          optionButton.type = 'button';
          optionButton.className = 'lunicora-variant-picker__menu-option';
          optionButton.dataset.optionMenuIndex = String(optionIndex);
          optionButton.dataset.optionMenuValue = value;
          optionButton.setAttribute('role', 'option');
          optionButton.setAttribute('aria-pressed', 'false');
          optionButton.textContent = value;
          menu.appendChild(optionButton);
        });

        values.append(triggerButton, menu);
        group.appendChild(values);
        optionsContainer.appendChild(group);
        return;
      }

      const select = document.createElement('select');
      select.className = 'lunicora-variant-picker__select';
      select.dataset.optionSelectIndex = String(optionIndex);
      select.setAttribute('aria-label', option.name || `Option ${optionIndex + 1}`);

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = `Choose ${option.name || `option ${optionIndex + 1}`}`;
      select.appendChild(placeholder);
      (option.values || []).forEach((rawValue) => {
        const value = normalizeOptionValue(rawValue);
        const optionElement = document.createElement('option');
        optionElement.value = value;
        optionElement.textContent = value;
        select.appendChild(optionElement);
      });
      values.appendChild(select);
      group.appendChild(values);
      optionsContainer.appendChild(group);
    });

    customOptions.forEach((option) => {
      const group = document.createElement('fieldset');
      group.className = 'lunicora-variant-picker__option';
      group.dataset.customOptionName = option.name;
      if (String(option.name || '').trim().toLowerCase().includes('color')) {
        group.classList.add('lunicora-variant-picker__option--color');
      }

      const legend = document.createElement('legend');
      legend.textContent = option.name;
      group.appendChild(legend);

      if (option.kind === 'text') {
        const wrap = document.createElement('div');
        wrap.className = 'lunicora-variant-picker__name-input-wrap';
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'lunicora-variant-picker__text-input';
        input.dataset.customOptionTextKey = option.key;
        input.placeholder = option.placeholder || option.name;
        input.autocomplete = 'off';
        if (option.maxLength) input.maxLength = option.maxLength;
        input.value = customSelections[option.key] || '';
        wrap.appendChild(input);
        group.appendChild(wrap);
        optionsContainer.appendChild(group);
        return;
      }

      const values = document.createElement('div');
      values.className = 'lunicora-variant-picker__values lunicora-variant-picker__values--select';

      if (useMobileOptionMenus) {
        const menuKey = `custom-${option.key}`;
        const triggerButton = document.createElement('button');
        triggerButton.type = 'button';
        triggerButton.className = 'lunicora-variant-picker__menu-trigger';
        triggerButton.dataset.variantPickerOptionTrigger = menuKey;
        triggerButton.dataset.optionPlaceholder = `Choose ${option.name}`;
        triggerButton.setAttribute('aria-expanded', 'false');
        triggerButton.setAttribute('aria-haspopup', 'listbox');
        triggerButton.textContent = triggerButton.dataset.optionPlaceholder;

        const menu = document.createElement('div');
        menu.className = 'lunicora-variant-picker__menu';
        menu.dataset.variantPickerOptionMenu = menuKey;
        menu.dataset.customOptionMenuKey = option.key;
        menu.setAttribute('role', 'listbox');
        menu.hidden = true;

        option.values.forEach((optionValue) => {
          const optionButton = document.createElement('button');
          optionButton.type = 'button';
          optionButton.className = 'lunicora-variant-picker__menu-option';
          optionButton.dataset.customOptionMenuKey = option.key;
          optionButton.dataset.customOptionMenuValue = optionValue.label;
          optionButton.setAttribute('role', 'option');
          optionButton.setAttribute('aria-pressed', 'false');
          optionButton.textContent = optionValue.label;
          menu.appendChild(optionButton);
        });

        values.append(triggerButton, menu);
        group.appendChild(values);
        optionsContainer.appendChild(group);
        return;
      }

      const select = document.createElement('select');
      select.className = 'lunicora-variant-picker__select';
      select.dataset.customOptionSelectKey = option.key;
      select.setAttribute('aria-label', option.name);

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = `Choose ${option.name}`;
      select.appendChild(placeholder);
      option.values.forEach((optionValue) => {
        const optionElement = document.createElement('option');
        optionElement.value = optionValue.label;
        optionElement.textContent = optionValue.label;
        select.appendChild(optionElement);
      });
      values.appendChild(select);
      group.appendChild(values);
      optionsContainer.appendChild(group);
    });

    picker.hidden = false;
    document.documentElement.classList.add('lunicora-variant-picker-open');
    updatePicker();
    requestAnimationFrame(() => picker.querySelector('.lunicora-variant-picker__close')?.focus());
  };

  const submitRecommendation = (card, variantId = null, customName = '', customProperties = {}) => {
    const input = card.querySelector('[data-recommendation-variant-id]');
    const nameProperty = card.querySelector('[data-recommendation-name-property]');
    const form = card.querySelector('.lunicora-recommendation-form');
    const submit = card.querySelector('[data-recommendation-submit]');
    if (!input || !submit) return;
    if (variantId != null) input.value = String(variantId);
    if (nameProperty) {
      const normalizedName = customName.trim();
      nameProperty.value = normalizedName;
      nameProperty.disabled = normalizedName.length === 0;
    }
    if (form) {
      form.querySelectorAll('[data-recommendation-custom-property]').forEach((propertyInput) => propertyInput.remove());
      Object.entries(customProperties).forEach(([propertyName, propertyValue]) => {
        if (!propertyName || !propertyValue) return;
        const propertyInput = document.createElement('input');
        propertyInput.type = 'hidden';
        propertyInput.name = `properties[${propertyName}]`;
        propertyInput.value = propertyValue;
        propertyInput.dataset.recommendationCustomProperty = '';
        form.appendChild(propertyInput);
      });
    }
    if (form?.requestSubmit) form.requestSubmit(submit);
    else submit.click();
    scheduleRecommendationReconcile({ productId: card.dataset.recommendationProductId });
  };

  const trapPickerFocus = (event) => {
    if (event.key !== 'Tab' || !pickerState) return;
    const focusable = Array.from(pickerState.picker.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  document.addEventListener('click', (event) => {
    const carouselButton = event.target.closest('[data-carousel-previous], [data-carousel-next]');
    if (carouselButton) {
      const { carousel, viewport, card } = getCarouselParts(carouselButton);
      if (!carousel || !viewport || !card) return;
      const track = viewport.querySelector('.lunicora-featured__track');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const direction = carouselButton.hasAttribute('data-carousel-previous') ? -1 : 1;
      const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const shouldLoop = carousel.dataset.carouselLoop === 'true';
      const isAtStart = viewport.scrollLeft <= 1;
      const isAtEnd = viewport.scrollLeft >= maximum - 1;

      if (shouldLoop && direction < 0 && isAtStart) {
        viewport.scrollTo({ left: maximum, behavior: 'smooth' });
      } else if (shouldLoop && direction > 0 && isAtEnd) {
        viewport.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        viewport.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
      }
      return;
    }

    const recommendationButton = event.target.closest('[data-recommendation-add]');
    if (recommendationButton) {
      const card = recommendationButton.closest('[data-recommendation-card]');
      if (!card) return;
      void openPicker(card, recommendationButton);
      return;
    }

    const optionMenuTrigger = event.target.closest('[data-variant-picker-option-trigger]');
    if (optionMenuTrigger && pickerState) {
      togglePickerOptionMenu(optionMenuTrigger);
      return;
    }

    const optionMenuValue = event.target.closest('[data-option-menu-value]');
    if (optionMenuValue && pickerState) {
      const optionIndex = Number(optionMenuValue.dataset.optionMenuIndex);
      pickerState.selections[optionIndex] = optionMenuValue.dataset.optionMenuValue;
      for (let index = optionIndex + 1; index < pickerState.selections.length; index += 1) {
        pickerState.selections[index] = null;
      }
      closePickerOptionMenus();
      updatePicker();
      return;
    }

    const customOptionMenuValue = event.target.closest('[data-custom-option-menu-value]');
    if (customOptionMenuValue && pickerState) {
      pickerState.customSelections[customOptionMenuValue.dataset.customOptionMenuKey]
        = customOptionMenuValue.dataset.customOptionMenuValue;
      closePickerOptionMenus();
      updatePicker();
      return;
    }

    const optionButton = event.target.closest('[data-option-index]');
    if (optionButton && pickerState) {
      const optionIndex = Number(optionButton.dataset.optionIndex);
      pickerState.selections[optionIndex] = optionButton.dataset.optionValue;
      for (let index = optionIndex + 1; index < pickerState.selections.length; index += 1) {
        pickerState.selections[index] = null;
      }
      updatePicker();
      return;
    }

    const customOptionButton = event.target.closest('[data-custom-option-key]');
    if (customOptionButton && pickerState) {
      pickerState.customSelections[customOptionButton.dataset.customOptionKey] = customOptionButton.dataset.customOptionValue;
      updatePicker();
      return;
    }

    if (event.target.closest('[data-variant-picker-close]')) {
      closePicker();
      return;
    }

    const pickerSubmit = event.target.closest('[data-variant-picker-submit]');
    if (pickerSubmit && pickerState) {
      if (pickerState.externalPersonalizer) {
        if (pickerState.product.url) window.location.assign(pickerState.product.url);
        return;
      }
      const exactVariant = getExactVariant();
      if (!exactVariant?.available) return;
      const card = pickerState.card;
      const customName = pickerState.picker.querySelector('[data-variant-picker-name]')?.value || '';
      if (pickerState.nameRequired && !customName.trim()) return;
      submitRecommendation(card, exactVariant.id, customName, pickerState.customSelections);
      closePicker({ returnFocus: false });
    }
  });

  document.addEventListener('input', (event) => {
    const textInput = event.target.closest('[data-custom-option-text-key]');
    if (textInput && pickerState) {
      pickerState.customSelections[textInput.dataset.customOptionTextKey] = textInput.value.trim() || null;
      updatePicker();
      return;
    }
    const nameInput = event.target.closest('[data-variant-picker-name]');
    if (!nameInput) return;
    const nameCount = nameInput.closest('[data-variant-picker]')?.querySelector('[data-variant-picker-name-count]');
    if (nameCount) nameCount.textContent = `${nameInput.value.length}/12`;
    if (pickerState?.nameRequired) updatePicker();
  });

  document.addEventListener('change', (event) => {
    const optionSelect = event.target.closest('[data-option-select-index]');
    if (optionSelect && pickerState) {
      const optionIndex = Number(optionSelect.dataset.optionSelectIndex);
      pickerState.selections[optionIndex] = optionSelect.value || null;
      for (let index = optionIndex + 1; index < pickerState.selections.length; index += 1) {
        pickerState.selections[index] = null;
      }
      updatePicker();
      return;
    }

    const customOptionSelect = event.target.closest('[data-custom-option-select-key]');
    if (!customOptionSelect || !pickerState) return;
    pickerState.customSelections[customOptionSelect.dataset.customOptionSelectKey] = customOptionSelect.value || null;
    updatePicker();
  });

  document.addEventListener('keydown', (event) => {
    if (!pickerState) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closePicker();
      return;
    }
    trapPickerFocus(event);
  });

  const initialize = (root = document) => initializeCarousels(root);
  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('resize', () => initializeCarousels(), { passive: true });

  const observeCart = () => {
    if (!document.body || document.body.dataset.lunicoraObserverReady === 'true') return;
    document.body.dataset.lunicoraObserverReady = 'true';

    const observer = new MutationObserver((mutations) => {
      if (pickerState && !pickerState.picker.isConnected) closePicker({ returnFocus: false });
      let recommendationsWereRendered = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          initialize(node);
          if (node.matches?.('[data-recommendation-card], [data-lunicora-carousel], .cart-drawer-item, #CartDrawer-CartItems')
            || node.querySelector?.('[data-recommendation-card], .cart-drawer-item')) {
            recommendationsWereRendered = true;
          }
        });
      });
      if (recommendationsWereRendered) scheduleRecommendationReconcile();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observeCart();
      scheduleRecommendationReconcile();
    });
  } else {
    observeCart();
    scheduleRecommendationReconcile();
  }
})();
